import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../components/ThemeProvider';
import Button from '../common/Button';
import { fetchData } from '../../utils/api';

function CartSummary() {
  const { cart } = useCart();
  const { primaryColor } = useTheme();

  const [business, setBusiness] = useState({
    name: '',
    abn: '',
  });

  const [vatRate, setVatRate] = useState(0);
  const [shippingFee, setShippingFee] = useState(null);
  const [error, setError] = useState('');
  const [isRetrying, setIsRetrying] = useState(false);

  const money = (value) =>
    new Intl.NumberFormat('en-SL', {
      style: 'currency',
      currency: 'SLE',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  const fetchBusinessDetails = async (retrying = false) => {
    if (retrying) {
      setIsRetrying(true);
    }

    try {
      const data = await fetchData(
        '/website/business-details',
        {
          noCache: true,
        },
      );

      if (!data?.business) {
        throw new Error(
          'Business details are missing from the response.',
        );
      }

      setBusiness(
        data.business || {
          name: 'Business',
          abn: '',
        },
      );

      setVatRate(
        Number(data?.fees?.vat?.vatRate || 0),
      );

      setShippingFee(
        Number(data?.fees?.shipping?.shippingFee || 0),
      );

      setError('');
    } catch (requestError) {
      console.error(
        'CartSummary: Failed to fetch business details:',
        requestError,
      );

      setError(
        'Failed to load business details or fees. Using default values.',
      );

      setBusiness({
        name: 'Business',
        abn: '',
      });

      setVatRate(0);
      setShippingFee(0);
    } finally {
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    fetchBusinessDetails();
  }, []);

  const handleRetry = () => {
    setError('');
    fetchBusinessDetails(true);
  };

  /*
   * Calculate the subtotal directly from raw cart prices.
   *
   * This avoids:
   * 1. getTotal() applying an unexpected conversion.
   * 2. formatPrice() applying another exchange rate.
   */
  const subtotal = useMemo(() => {
    return (cart || []).reduce((total, item) => {
      const unitPrice = Number(item.price || 0);
      const quantity = Number(item.quantity || 0);

      return total + unitPrice * quantity;
    }, 0);
  }, [cart]);

  const safeVatRate = Number(vatRate || 0);
  const shipping = Number(shippingFee || 0);

  const vat = subtotal * (safeVatRate / 100);
  const total = subtotal + vat + shipping;

  const cartMerchant = cart.find(
    (item) => item.merchant_id,
  );

  const summaryBusinessName =
    cartMerchant?.merchant_name ||
    business.name ||
    'Business';

  const summaryBusinessPhone =
    cartMerchant?.merchant_phone ||
    business.phone ||
    business.abn ||
    '';

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-md">
      <h2 className="mb-2 font-playfair-display text-2xl font-semibold text-primary">
        Order Summary
      </h2>

      <p className="mb-4 font-montserrat text-sm text-primary/70">
        {summaryBusinessName}

        {summaryBusinessPhone
          ? ` (TEL: ${summaryBusinessPhone})`
          : ''}
      </p>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="font-montserrat text-sm text-red-600">
            {error}
          </p>

          <Button
            onClick={handleRetry}
            className="mt-2 rounded-full bg-primary px-4 py-2 font-montserrat text-sm text-cream hover:bg-primary/90"
            disabled={isRetrying}
            aria-label="Retry loading business details"
            style={{ '--primary': primaryColor }}
          >
            {isRetrying ? 'Retrying...' : 'Retry'}
          </Button>
        </div>
      )}

      <div className="mb-4 space-y-3 font-montserrat text-secondary">
        <div className="flex justify-between text-lg">
          <span>Subtotal</span>
          <span>{money(subtotal)}</span>
        </div>

        <div className="flex justify-between text-lg">
          <span>VAT ({safeVatRate}%)</span>
          <span>{money(vat)}</span>
        </div>

        <div className="flex justify-between text-lg">
          <span>Shipping</span>

          <span>
            {shippingFee === null
              ? 'Calculated at checkout'
              : money(shipping)}
          </span>
        </div>

        <div className="flex justify-between border-t border-primary/20 pt-3 text-xl font-semibold">
          <span>Total</span>
          <span>{money(total)}</span>
        </div>
      </div>

      <Link
        to="/checkout"
        className={
          cart.length === 0
            ? 'pointer-events-none'
            : ''
        }
      >
        <Button
          className="w-full rounded-full bg-primary px-6 py-3 font-montserrat text-lg text-cream hover:bg-primary/90 disabled:opacity-50"
          disabled={cart.length === 0}
          aria-label={
            cart.length === 0
              ? 'Checkout disabled because cart is empty'
              : 'Proceed to checkout'
          }
          style={{ '--primary': primaryColor }}
        >
          Proceed to Checkout
        </Button>
      </Link>

      {cart.length > 0 && (
        <Link
          to="/shop"
          className="mt-4 block text-center font-montserrat text-primary hover:underline"
          aria-label="Continue shopping"
        >
          Continue Shopping
        </Link>
      )}
    </div>
  );
}

export default CartSummary;